import { debounce, isEmpty, isEqual, noop, omitBy } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { JsonParam, useQueryParams } from 'use-query-params';

import { DateRange } from 'dashboard/components-lib/DashboardDatePicker/DashboardDatePicker';
import { DEFAULT_PAGE_SIZE } from 'dashboard/lib/constants/pagination';
import { BaseFilterParams } from 'dashboard/lib/interfaces/BaseFilterParams';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { RootState } from 'dashboard/redux/store';
import usePrevious from 'lib/hooks/usePrevious';
import { getDateRange } from 'lib/utils/filters';

interface UseTableArgs<FilterParams extends BaseFilterParams = BaseFilterParams>
  extends UseTableCallbacksArgs<FilterParams> {
  filters?: FilterParams;
  sortParams?: SortParams;
  pageSize?: number;
  currentPage?: number;
  key?: string;
}

export interface UseTableReturn extends UseTableCallbacksReturn {
  // Because useTable reads from query parameters to initialize state through
  // state setters -- UI's that use this hook may want more control over when
  // they are ready to run expensive operations like fetching data based on
  // these parameters.
  isReady: boolean;
}

interface TableQueryParams<FilterParams extends BaseFilterParams = BaseFilterParams> {
  filter?: FilterParams;
  sort?: SortParams;
  pageSize?: number;
  currentPage?: number;
}

/**
 * @name useTable
 *
 * A hook that handles syncing of sort/filter/pagination state and URL
 * query string, and wrapping of state update callbacks with sensible
 * default behavior.
 *
 * When run, this hook sets up two effects. The first runs only on mount and
 * syncs the sort/filter/pagination state to the URL. The second runs on
 * sort/filter/pagination state change and syncs the URL back to state.
 *
 * This hook also returns some optional callbacks that wrap action creators
 * with some sane defaults.
 */
export default function <FilterParams extends BaseFilterParams>({
  filters,
  sortParams,
  pageSize,
  currentPage,
  setFilters = noop,
  setSortParams = noop,
  setPageSize = noop,
  setCurrentPage = noop,
  key,
}: UseTableArgs<FilterParams>): UseTableReturn {
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);

  const currentEnvironmentId = useSelector<RootState, string>(
    (state) => state.currentEnvironment.currentEnvironment?.id,
  );

  const [query, setQuery] = useQueryParams(
    key != null
      ? { [key]: JsonParam }
      : {
          filter: JsonParam,
          sort: JsonParam,
          pageSize: JsonParam,
          currentPage: JsonParam,
        },
  );

  /**
   * This effect is run only on mount, and uses the URL as the source of truth.
   * It syncs Redux filter/pagination state with what is in the URL.
   *
   * Note that filter and sort setting might be no-ops, if these don't exist.
   *
   * We have to do a lot of type casting because useQueryParams doesn't handle
   * the case of nested params well.
   */
  useEffect(() => {
    const keyedParams: TableQueryParams<FilterParams> =
      key != null ? (query as Record<string, TableQueryParams<FilterParams>>)[key] || {} : query;

    const urlFilters = keyedParams.filter || filters;
    const urlSortParams = keyedParams.sort || sortParams;
    const urlPageSize = keyedParams.pageSize || pageSize || DEFAULT_PAGE_SIZE;
    const urlCurrentPage = keyedParams.currentPage || currentPage || 1;

    // We remove any `undefined` filter values first, because these are not encoded
    // into the URL when filter values are set. We also need to remove `undefined`
    // filter values from urlFilters because these will mirror filters when there are
    // no URL parameters.
    const compactFilters = omitBy((v) => v == null, filters);
    const compactUrlFilters = omitBy((v) => v == null, urlFilters);

    // Deep equality check because shallow eq will never pass when serializing from URL
    // This helps us avoid effects (usually fetches) that trigger when filters change
    // We also check for emptiness to avoid a needless first fetch on app load.
    if (!isEmpty(compactUrlFilters) && !isEqual(compactUrlFilters, compactFilters)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setFilters(urlFilters);
    }

    if (!isEmpty(urlSortParams) && !isEqual(urlSortParams, sortParams)) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setSortParams(urlSortParams);
    }

    setPageSize(urlPageSize);
    setCurrentPage(urlCurrentPage);
    setIsReady(true);
  }, []);

  const previousEnvironmentId = usePrevious(currentEnvironmentId);

  // Reset pagination when toggling sandbox / prod
  useEffect(() => {
    // only run this effect if environment is changed, don't run on mount / unmount
    if (previousEnvironmentId !== currentEnvironmentId && previousEnvironmentId != null)
      setCurrentPage(1);
  }, [previousEnvironmentId, currentEnvironmentId]);

  /**
   * This effect is run on prop change, and uses props as the source of truth.
   * It syncs the URL query string with Redux filter/pagination state.
   */
  useEffect(() => {
    // change url to include search params
    const newParams = {
      filter: filters,
      sort: sortParams,
      currentPage: currentPage,
      pageSize: pageSize,
    };
    const keyedParams = key != null ? { [key]: newParams } : newParams;

    setQuery(keyedParams);
  }, [filters, sortParams, pageSize, currentPage, location.pathname]);

  const paginationCallbacks = useTableCallbacks({
    setFilters,
    setSortParams,
    setPageSize,
    setCurrentPage,
  });

  return {
    ...paginationCallbacks,
    isReady,
  };
}

interface UseTableCallbacksArgs<FilterParams extends BaseFilterParams> {
  setFilters?: (filters: FilterParams) => void;
  setSortParams?: (sortParams: SortParams) => void;
  setCurrentPage?: (currentPage: number) => void;
  setPageSize?: (pageSize: number) => void;
}

type HandleDateUpdate = (
  range: DateRange,
  options?: {
    /**
     * If the range should support time based filtering
     */
    time?: boolean;
  },
) => void;

export interface UseTableCallbacksReturn {
  handlePageLinkClick: (newPageNumber: number) => void;
  handlePageSizeClick: (newPageSize: number) => void;
  handleDateUpdate: HandleDateUpdate;
  handleSortUpdate: (newSortParams: SortParams) => void;
  handleOmniSearch: (omniQuery: string) => void;
}

/**
 * @name useTableCallbacks
 *
 * A hook that wraps common callbacks needed for sorting, filtering, and
 * pagination. This increases consistency by always running page cleanup such
 * as resetting the page count when changing filters.
 */
function useTableCallbacks<FilterParams extends BaseFilterParams>({
  setFilters = noop,
  setSortParams = noop,
  setPageSize = noop,
  setCurrentPage = noop,
}: UseTableCallbacksArgs<FilterParams>): UseTableCallbacksReturn {
  const handlePageLinkClick = useCallback(
    (newPageNumber: number) => {
      setCurrentPage(newPageNumber);
    },
    [setCurrentPage],
  );

  const handlePageSizeClick = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);

      // Send back to the first page to avoid situation where a higher page size does not
      // have current page number.
      setCurrentPage(1);
    },
    [setPageSize, setCurrentPage],
  );

  const handleDateUpdate = useCallback<HandleDateUpdate>(
    ({ startDate, endDate }, options) => {
      if (startDate || endDate) {
        if (options?.time) {
          setFilters({ createdAtStart: startDate, createdAtEnd: endDate } as FilterParams);
        } else {
          const { start, end } = getDateRange(startDate, endDate);
          setFilters({ createdAtStart: start, createdAtEnd: end } as FilterParams);
        }
      } else {
        setFilters({
          createdAtStart: undefined,
          createdAtEnd: undefined,
        } as FilterParams);
      }
      setCurrentPage(1);
    },
    [setFilters, setCurrentPage],
  );

  const handleSortUpdate = useCallback(
    (sortParams: SortParams) => {
      setSortParams(sortParams);
      setCurrentPage(1);
    },
    [setSortParams, setCurrentPage],
  );

  const updateOmniQuery = useCallback(
    (omniQuery: string) => {
      setFilters({
        omni: omniQuery,
      } as FilterParams);
      setCurrentPage(1);
    },
    [setFilters, setCurrentPage],
  );

  const handleOmniSearch = useMemo(() => debounce(500, updateOmniQuery), [updateOmniQuery]);

  return {
    handlePageLinkClick,
    handlePageSizeClick,
    handleDateUpdate,
    handleSortUpdate,
    handleOmniSearch,
  };
}
